import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  Shield as ShieldIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  HowToReg as ApproveIcon,
  Block as RejectIcon,
  Psychology as AiIcon,
} from '@mui/icons-material';
import { prescriptionSafety } from '../../lib/api';
import type { PrescriptionSafetyResponse, AiRiskLevel, SafetyFinding } from '../../lib/api';
import { useTranslation } from 'react-i18next';

interface PrescriptionSafetyCheckProps {
  prescriptionId: string | null;
  /** If false, button is hidden (permission not granted) */
  hasPermission: boolean;
  /** If true, button is shown but disabled (e.g. no medications entered yet) */
  disabled?: boolean;
  /** Called when no prescriptionId yet — should auto-save draft and return the new id */
  onAutoSave?: () => Promise<string | null>;
  /**
   * A value that changes whenever the medication list changes (e.g. a serialized snapshot).
   * When provided, the check auto-runs ~2.5s after this stabilizes — the same deterministic rule
   * engine used by the RAG prescribing reference, so this stays free/cheap even run automatically.
   * The manual button still works for an explicit re-run.
   */
  autoRunSignal?: string;
}

const RISK_CONFIG: Record<AiRiskLevel, { color: 'error' | 'warning' | 'info' | 'success'; label: string; icon: React.ReactElement }> = {
  CRITICAL: { color: 'error',   label: 'Critical',  icon: <ErrorIcon   fontSize="small" /> },
  HIGH:     { color: 'error',   label: 'High Risk',  icon: <WarningIcon fontSize="small" /> },
  MODERATE: { color: 'warning', label: 'Moderate',   icon: <WarningIcon fontSize="small" /> },
  LOW:      { color: 'success', label: 'Low Risk',   icon: <CheckIcon   fontSize="small" /> },
};

export const PrescriptionSafetyCheck: React.FC<PrescriptionSafetyCheckProps> = ({
  prescriptionId,
  hasPermission,
  disabled = false,
  onAutoSave,
  autoRunSignal,
}) => {
  const { t } = useTranslation('prescriptions');
  const [loading, setLoading]           = useState(false);
  const [result, setResult]             = useState<PrescriptionSafetyResponse | null>(null);
  const [error, setError]               = useState<string | null>(null);
  const [expanded, setExpanded]         = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');
  const [approvalAction, setApprovalAction]   = useState<'approve' | 'reject' | null>(null);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalDone, setApprovalDone]       = useState(false);
  const [autoChecking, setAutoChecking]       = useState(false);
  const lastAutoRunSignal = useRef<string | null>(null);
  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  const handleRunCheck = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setApprovalDone(false);
    try {
      let idToUse = prescriptionId;
      if (!idToUse && onAutoSave) {
        idToUse = await onAutoSave();
      }
      if (!idToUse) {
        setError('Could not save prescription. Please try again.');
        return;
      }
      const res = await prescriptionSafety.runCheck(idToUse);
      if (res.data) {
        setResult(res.data);
        setExpanded(true);
      } else {
        setError(res.error ?? 'Safety check failed');
      }
    } catch (e: any) {
      setError(e.message ?? 'Safety check unavailable');
    } finally {
      setLoading(false);
    }
  };

  // Auto-run ~2.5s after the medication list stabilizes (debounced), so the doctor sees safety
  // findings without an extra click — the manual button below still works for an explicit re-run.
  useEffect(() => {
    if (!hasPermission || disabled || !autoRunSignal || !autoRunSignal.trim()) return;
    if (lastAutoRunSignal.current === autoRunSignal) return;
    const timer = setTimeout(() => {
      if (loadingRef.current) return;
      lastAutoRunSignal.current = autoRunSignal;
      setAutoChecking(true);
      handleRunCheck().finally(() => setAutoChecking(false));
    }, 2500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRunSignal, hasPermission, disabled]);

  if (!hasPermission) return null;

  const handleApprovalSubmit = async () => {
    if (!result?.approvalId || !approvalAction) return;
    setApprovalLoading(true);
    try {
      if (approvalAction === 'approve') {
        await prescriptionSafety.approve(result.approvalId, approvalComment);
      } else {
        if (!approvalComment.trim()) return;
        await prescriptionSafety.reject(result.approvalId, approvalComment);
      }
      setApprovalDone(true);
      setApprovalOpen(false);
    } catch {
      // keep dialog open on failure
    } finally {
      setApprovalLoading(false);
    }
  };

  const riskCfg = result ? RISK_CONFIG[result.finalRisk] : null;

  return (
    <Box>
      {/* Trigger button */}
      <Tooltip title={disabled ? 'Enter at least one medication to enable safety check' : ''} placement="top">
        <span>
          <Button
            variant="outlined"
            size="small"
            startIcon={loading ? <CircularProgress size={14} /> : <ShieldIcon fontSize="small" />}
            onClick={handleRunCheck}
            disabled={loading || disabled}
            color={result ? riskCfg?.color ?? 'primary' : 'primary'}
            sx={{ textTransform: 'none' }}
          >
            {loading ? (autoChecking ? 'Auto-checking...' : 'Checking...') : result ? 'Re-run Safety Check' : 'AI Safety Check'}
          </Button>
        </span>
      </Tooltip>

      {/* Error */}
      {error && (
        <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Result card */}
      {result && (
        <Box sx={{ mt: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {/* Header */}
          <Box
            sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                  bgcolor: result.finalRisk === 'CRITICAL' || result.finalRisk === 'HIGH'
                      ? 'error.50' : result.finalRisk === 'MODERATE' ? 'warning.50' : 'success.50' }}
            onClick={() => setExpanded(!expanded)}
          >
            {riskCfg?.icon}
            <Chip
              label={riskCfg?.label}
              color={riskCfg?.color}
              size="small"
              sx={{ fontWeight: 700 }}
            />
            {result.cacheHit && (
              <Chip label="cached" size="small" variant="outlined" sx={{ fontSize: 10 }} />
            )}
            <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }}>
              {result.safetyNarrative}
            </Typography>
            {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
          </Box>

          <Collapse in={expanded}>
            <Box sx={{ p: 1.5 }}>

              {/* Source breakdown */}
              <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                <Chip label={`Rule Engine: ${result.ruleEngineRisk}`} size="small" variant="outlined" />
                {result.status !== 'BLOCKED_BY_RULE_ENGINE' && (
                  <Chip label={`AI: ${result.aiRisk}`} size="small" variant="outlined"
                        icon={<AiIcon fontSize="small" />} />
                )}
                <Chip label={`Status: ${result.status}`} size="small" variant="outlined" />
              </Box>

              {/* Findings */}
              {result.findings.length > 0 && (
                <>
                  <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
                    Findings ({result.findings.length})
                  </Typography>
                  <List dense disablePadding>
                    {result.findings.map((f, i) => (
                      <FindingRow key={i} finding={f} />
                    ))}
                  </List>
                  <Divider sx={{ my: 1 }} />
                </>
              )}

              {/* Recommendations */}
              {result.recommendations.length > 0 && (
                <>
                  <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
                    {t('recommendations')}
                  </Typography>
                  {result.recommendations.map((r, i) => (
                    <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
                      • {r}
                    </Typography>
                  ))}
                </>
              )}

              {/* Approval buttons */}
              {result.doctorApprovalRequired && !approvalDone && result.approvalId && (
                <Box display="flex" gap={1} mt={1.5}>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<ApproveIcon />}
                    onClick={() => { setApprovalAction('approve'); setApprovalOpen(true); }}
                  >
                    {t('approveAndAmpProceed')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<RejectIcon />}
                    onClick={() => { setApprovalAction('reject'); setApprovalOpen(true); }}
                  >
                    {t('overrideWithReason')}
                  </Button>
                </Box>
              )}
              {approvalDone && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  {approvalAction === 'approve' ? 'Approval recorded.' : 'Override recorded with reason.'}
                </Alert>
              )}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Approval dialog */}
      <Dialog open={approvalOpen} onClose={() => setApprovalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {approvalAction === 'approve' ? 'Approve Safety Findings' : 'Override Safety Findings'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {approvalAction === 'approve'
              ? 'You are confirming you have reviewed the safety findings and approve this prescription.'
              : 'You are overriding the safety findings. A documented reason is required.'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={approvalAction === 'approve' ? 'Comments (optional)' : 'Reason for override (required)'}
            value={approvalComment}
            onChange={(e) => setApprovalComment(e.target.value)}
            required={approvalAction === 'reject'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalOpen(false)} disabled={approvalLoading}>{t('cancel')}</Button>
          <Button
            variant="contained"
            color={approvalAction === 'approve' ? 'success' : 'error'}
            onClick={handleApprovalSubmit}
            disabled={approvalLoading || (approvalAction === 'reject' && !approvalComment.trim())}
          >
            {approvalLoading ? <CircularProgress size={16} /> : (approvalAction === 'approve' ? 'Confirm Approval' : 'Submit Override')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─────────────────────── Finding row ─────────────────────────────────────────

const FindingRow: React.FC<{ finding: SafetyFinding }> = ({ finding }) => {
  const cfg = RISK_CONFIG[finding.severity] ?? RISK_CONFIG.MODERATE;
  return (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemIcon sx={{ minWidth: 28 }}>
        <Tooltip title={finding.severity}>
          {React.cloneElement(cfg.icon, { color: cfg.color === 'success' ? 'success' : cfg.color === 'warning' ? 'warning' : 'error' })}
        </Tooltip>
      </ListItemIcon>
      <ListItemText
        primary={
          <Box component="span" display="flex" alignItems="center" gap={0.5}>
            <Typography variant="body2" component="span">{finding.message}</Typography>
            {finding.source === 'AI' && (
              <Chip label="AI" size="small" variant="outlined" sx={{ fontSize: 9, height: 16 }} />
            )}
          </Box>
        }
        secondary={finding.medicationName ? `Medicine: ${finding.medicationName}` : undefined}
        primaryTypographyProps={{ variant: 'body2' }}
        secondaryTypographyProps={{ variant: 'caption' }}
      />
    </ListItem>
  );
};
